/**
 * Phase-1 inline starter catalog — 15 curated hunting patterns. Used as a
 * fallback when `/research/starter-hunts` isn't live yet, and as the source
 * of truth for the "Browse all 15 starter hunts" expander.
 */

export interface StarterHunt {
  id: string;
  name: string;
  description: string;
  queryDsl: string;
  category?: string;
  mitreTactic?: string;
  mitreTechId?: string;
  platforms?: string[];
  icon?: string;
}

export const STARTER_HUNTS: StarterHunt[] = [
  {
    id: "starter_powershell_non_it",
    name: "PowerShell on non-IT devices",
    description:
      "Every PowerShell exec — classic lateral-movement and living-off-the-land pattern.",
    queryDsl: "event_type:exec AND process.path:*powershell*",
    category: "Execution",
    mitreTactic: "Execution",
    mitreTechId: "T1059.001",
    platforms: ["windows", "macos", "linux"],
    icon: "🔍",
  },
  {
    id: "starter_launchagent_drift",
    name: "LaunchAgent persistence drift",
    description: "File writes into LaunchAgents — a top-3 macOS persistence surface.",
    queryDsl: "event_type:file AND raw_payload.target_path:*LaunchAgents*",
    category: "Persistence",
    mitreTactic: "Persistence",
    mitreTechId: "T1547.011",
    platforms: ["macos"],
    icon: "🪤",
  },
  {
    id: "starter_dotenv_access",
    name: "Credential file access by unsigned binary",
    description:
      ".env file reads where the reading process has no Team ID — likely credential theft.",
    queryDsl:
      'event_type:file AND raw_payload.target_path:*\\.env* AND process.team_id:""',
    category: "Credential Access",
    mitreTactic: "Credential Access",
    mitreTechId: "T1552.001",
    platforms: ["macos", "linux"],
    icon: "🔑",
  },
  {
    id: "starter_mcp_spawn_non_ide",
    name: "MCP server spawn from non-IDE parent",
    description:
      "An MCP server starting outside a known IDE — could be a stowaway agent.",
    queryDsl:
      "event_type:exec AND process.path:*mcp-server* NOT parent.process.path:*Cursor*",
    category: "Defense Evasion",
    mitreTactic: "Defense Evasion",
    mitreTechId: "T1059",
    platforms: ["macos", "linux"],
    icon: "🤖",
  },
  {
    id: "starter_zsh_to_xargs",
    name: "xargs from Claude / Cursor parent",
    description:
      "Catches AI agents shelling out to xargs — useful for hunting prompt-injection abuse.",
    queryDsl: "process.path:*xargs* AND parent.process.path:*claude*",
    category: "Execution",
    mitreTactic: "Execution",
    mitreTechId: "T1059",
    platforms: ["macos", "linux"],
    icon: "🪄",
  },
  {
    id: "starter_curl_pipe_sh",
    name: "curl | sh pattern",
    description: "Network fetch piped straight into a shell — install-script abuse.",
    queryDsl:
      "event_type:exec AND process.path:*sh AND parent.process.path:*curl*",
    category: "Initial Access",
    mitreTactic: "Initial Access",
    mitreTechId: "T1105",
    platforms: ["macos", "linux"],
    icon: "🌊",
  },
  {
    id: "starter_login_keychain_dump",
    name: "Login keychain access",
    description:
      "Reads against ~/Library/Keychains/login.keychain-db — likely credential dump.",
    queryDsl:
      "event_type:file AND raw_payload.target_path:*login.keychain*",
    category: "Credential Access",
    mitreTactic: "Credential Access",
    mitreTechId: "T1555.001",
    platforms: ["macos"],
    icon: "🔐",
  },
  {
    id: "starter_sudoers_write",
    name: "sudoers file modification",
    description:
      "Writes into /etc/sudoers* — privilege escalation primitive.",
    queryDsl:
      "event_type:file AND raw_payload.target_path:/etc/sudoers*",
    category: "Privilege Escalation",
    mitreTactic: "Privilege Escalation",
    mitreTechId: "T1548.003",
    platforms: ["macos", "linux"],
    icon: "⛰️",
  },
  {
    id: "starter_pam_module_swap",
    name: "PAM module write",
    description:
      "Writes into /etc/pam.d — replacing an auth module is a credential-theft classic.",
    queryDsl:
      "event_type:file AND raw_payload.target_path:/etc/pam.d/*",
    category: "Credential Access",
    mitreTactic: "Credential Access",
    mitreTechId: "T1556.003",
    platforms: ["macos", "linux"],
    icon: "🧱",
  },
  {
    id: "starter_ssh_authorized_keys",
    name: "New SSH authorized_keys entry",
    description:
      "Write into any ~/.ssh/authorized_keys — backdoor persistence and lateral movement.",
    queryDsl:
      "event_type:file AND raw_payload.target_path:*authorized_keys*",
    category: "Persistence",
    mitreTactic: "Persistence",
    mitreTechId: "T1098.004",
    platforms: ["macos", "linux"],
    icon: "🗝️",
  },
  {
    id: "starter_apple_phishing_threat",
    name: "Apple Web Threat Intelligence — phishing hits",
    description:
      "macOS Network Extension flagged a phishing destination on a managed Mac.",
    queryDsl:
      "raw_payload.threat:THREAT_TYPE_PHISHING",
    category: "Initial Access",
    mitreTactic: "Initial Access",
    mitreTechId: "T1566",
    platforms: ["macos"],
    icon: "🎣",
  },
  {
    id: "starter_unsigned_helper_in_libdir",
    name: "Unsigned helper in /Library/Application Support",
    description:
      "Process exec from /Library/Application Support with no Team ID — staging directory abuse.",
    queryDsl:
      'event_type:exec AND process.path:*/Library/Application\\ Support/* AND process.team_id:""',
    category: "Defense Evasion",
    mitreTactic: "Defense Evasion",
    mitreTechId: "T1036.005",
    platforms: ["macos"],
    icon: "🫥",
  },
  {
    id: "starter_browser_extension_drop",
    name: "Browser extension install drop",
    description:
      "File writes into the Chrome or Edge extensions directory — extension hijack.",
    queryDsl:
      "event_type:file AND raw_payload.target_path:*Extensions*",
    category: "Persistence",
    mitreTactic: "Persistence",
    mitreTechId: "T1176",
    platforms: ["macos", "windows", "linux"],
    icon: "🧩",
  },
  {
    id: "starter_osascript_to_shell",
    name: "AppleScript dropping a shell",
    description:
      "osascript invoking /bin/sh — common macOS payload-staging trick.",
    queryDsl:
      "event_type:exec AND process.path:*/bin/sh AND parent.process.path:*osascript",
    category: "Execution",
    mitreTactic: "Execution",
    mitreTechId: "T1059.002",
    platforms: ["macos"],
    icon: "🍎",
  },
  {
    id: "starter_remote_desktop_enable",
    name: "Remote desktop service enable",
    description:
      "Writes to /etc/RemoteManagement or screensharing prefs — remote-access enablement.",
    queryDsl:
      "event_type:file AND raw_payload.target_path:*RemoteManagement*",
    category: "Command and Control",
    mitreTactic: "Command and Control",
    mitreTechId: "T1219",
    platforms: ["macos"],
    icon: "🛰️",
  },
];
